import {
  CSSProperties,
  FC,
  FocusEvent,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { FormikProps } from "formik";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { useInView } from "react-intersection-observer";
import { Heading, Text } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { msg, t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useTheme, Theme } from "@emotion/react";
import { Button } from "src/atoms/Button";
import { ChoiceCard, ChoiceCardConfirmation } from "src/atoms/ChoiceCard";
import { Error } from "src/atoms/Input/Input.styled";
import { Field, StyledInput } from "src/atoms/Input/InputWithButton.styled";
import { Skeleton } from "src/atoms/Skeleton";
import { locale } from "src/config/app-config";
import { useAppActions, useSigningOut } from "src/hooks/stores/use-app-store";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import {
  CreateAccountFormValues,
  useCreateAccountForm,
} from "src/hooks/use-create-account-form";
import { useImageLoaded } from "src/hooks/use-image-loaded";
import { useIsClient } from "src/hooks/use-is-client";
import { MobileOS, useMobileOS } from "src/hooks/use-mobile-os";
import { useOptInForm } from "src/hooks/use-opt-in-form";
import { useReferFriend } from "src/hooks/use-refer-friend";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { PromotionEventType, useSegment } from "src/hooks/useSegment";
import { AndroidIcon } from "src/icons/AndroidIcon";
import { AppleIcon } from "src/icons/AppleIcon";
import { CheckCircleDrawIcon } from "src/icons/CheckCircleDrawIcon";
import { Eye } from "src/icons/Eye";
import { EyeSlash } from "src/icons/EyeSlash";
import { PageHead } from "src/molecules/PageHead";
import { ScreenLoader } from "src/molecules/ScreenLoader";
import { Stepper } from "src/molecules/Stepper";
import { SEGMENT_PROMOTION_TYPE } from "src/segment";
import {
  APP_DOWNLOAD_ORIGIN,
  APP_DOWNLOAD_STORE,
  trackAppDownloadButtonClickedEvent,
} from "src/tracking/events/app-download-button-clicked";
import { trackContinueShoppingClickedEvent } from "src/tracking/events/continue-shopping-clicked";
import { trackFeedbackFormAnsweredEvent } from "src/tracking/events/feedback-form-answered";
import { trackFeedbackFormCompletedEvent } from "src/tracking/events/feedback-form-completed";
import { trackFeedbackFormDisplayedEvent } from "src/tracking/events/feedback-form-displayed";
import { Origin } from "src/types/graphql.d";
import { appStoreUrl, googlePlayUrl } from "src/utils/constants";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { isOptedIn } from "src/utils/opt-in";
import {
  canCreateAccountOnConfirmation,
  getCheckoutCustomer,
  getCheckoutCustomerName,
  getStoredFeedback,
  rememberCheckoutOptIn,
  rememberCheckoutSignUp,
  storeFeedbackAnswer,
} from "./utils";
import {
  FEEDBACK_CONFIRMATION_DELAY_MS,
  FEEDBACK_FOLLOW_UP_DELAY_MS,
  FEEDBACK_FORMS,
  FEEDBACK_SUBMIT_DELAY_MS,
  FeedbackOption,
  StoredFeedback,
} from "./constants";
import {
  AppDownloadButtons,
  AppDownloadCard,
  AppDownloadQr,
  AppDownloadRow,
  ConfirmationCard,
  ContinueShopping,
  CreateAccountButton,
  CreateAccountCard,
  CreateAccountForm,
  FeedbackGrid,
  FeedbackRows,
  FollowUp,
  FollowUpActions,
  FollowUpLabel,
  FollowUpTextArea,
  NoticeRow,
  PasswordToggle,
  ReferAFriendBody,
  ReferAFriendCard,
  ReferAFriendImage,
  ReferAFriendLink,
  SemiboldEmail,
  SkipButton,
  SmallButton,
  StoreButton,
  SubscribeButton,
  SubscribeDisclaimer,
  SubscribeFormWrapper,
  UnderlinedLink,
} from "./CheckoutStepConfirmation.styled";
import styles from "./CheckoutStepConfirmation.module.scss";

const MOBILE_ORDER = {
  thanks: 1,
  appDownload: 2,
  subscribe: 3,
  survey: 4,
  referAFriend: 5,
};

const mobileOrder = (order: number): CSSProperties =>
  ({ "--ot-mobile-order": order } as CSSProperties);

const REFER_A_FRIEND_PROMOTION = {
  promotionType: SEGMENT_PROMOTION_TYPE.referAFriend,
  position: `${SEGMENT_PROMOTION_TYPE.referAFriend}-1`,
};

const STORES = {
  ios: {
    Icon: AppleIcon,
    store: APP_DOWNLOAD_STORE.appStore,
    testId: "checkout-confirmation-app-store",
  },
  android: {
    Icon: AndroidIcon,
    store: APP_DOWNLOAD_STORE.googlePlay,
    testId: "checkout-confirmation-google-play",
  },
} as const;

type SurveyStage = "asking" | "followUp" | "confirmed";

interface CheckoutCustomer {
  firstName?: string;
  email?: string;
  usedSignUpBlock: boolean;
  resolved: boolean;
}

const useCheckoutCustomer = (): CheckoutCustomer => {
  const { data: session } = useSession();
  const isClient = useIsClient();

  const handover = useMemo(
    () => (isClient ? getCheckoutCustomer() : null),
    [isClient]
  );

  return {
    firstName:
      session?.user.first_name ||
      handover?.firstName ||
      session?.user.name ||
      undefined,
    email: session?.user.email || handover?.email || undefined,
    usedSignUpBlock: handover?.usedSignUpBlock ?? false,
    resolved: isClient,
  };
};

const useHadOptedIn = (): { hadOptedIn: boolean; resolved: boolean } => {
  const { data: session, status } = useSession();
  const isClient = useIsClient();
  const hadOptedIn = useRef<boolean | null>(null);

  if (hadOptedIn.current === null && isClient && status !== "loading") {
    hadOptedIn.current =
      isOptedIn(session?.user.opt_in) || getCheckoutCustomer().optedIn === true;
  }

  return {
    hadOptedIn: hadOptedIn.current ?? false,
    resolved: hadOptedIn.current !== null,
  };
};

const ConfirmationNotice: FC<{ children: ReactNode; mt?: number }> = ({
  children,
  mt = 4,
}) => {
  const theme: Theme = useTheme();

  return (
    <NoticeRow
      alignItems="center"
      mt={mt}
      sx={{ gap: theme.space.space8 }}
      role="status"
    >
      <CheckCircleDrawIcon color={theme.colors.semantic.green} />
      <Text variant="body.source.regular16">{children}</Text>
    </NoticeRow>
  );
};

const ContinueShoppingButton: FC<{ sticky?: boolean }> = ({ sticky }) => {
  const { currentShopType } = useShopTypeContext();
  const shopType = localeFix({ gender: currentShopType });

  return (
    <Link
      href={getI18nRoute({ route: `/${shopType}`, locale })}
      passHref
      legacyBehavior
    >
      <Button
        as="a"
        variant={sticky ? "primary" : "secondary"}
        fullWidth={sticky}
        onClick={() => void trackContinueShoppingClickedEvent()}
        data-testid={`checkout-confirmation-continue-shopping${
          sticky ? "-sticky" : ""
        }`}
      >
        <Trans>Continue shopping</Trans>
      </Button>
    </Link>
  );
};

const ConfirmationThanks: FC<{
  firstName?: string;
  email?: string | null;
  isPaymentPending: boolean;
  createAccount?: ReactNode;
}> = ({ firstName, email, isPaymentPending, createAccount }) => (
  <>
    <Heading as="h1" variant="h2" sx={{ wordBreak: "break-word" }}>
      {firstName ? (
        <Trans>Thank you, {firstName}!</Trans>
      ) : (
        <Trans>Thank you!</Trans>
      )}
    </Heading>

    <Text
      as="p"
      mt={[3, 3, 3, 4]}
      variant="body.source.regular16"
      data-testid="checkout-confirmation-step-confirmation-text"
    >
      {email ? (
        isPaymentPending ? (
          <Trans>
            We’ll send your confirmation to{" "}
            <SemiboldEmail data-testid="checkout-confirmation-step-client-email">
              {email}
            </SemiboldEmail>{" "}
            once the payment is processed. We’ll follow up with tracking links
            once your items ship (which might come in a few separate packages).
          </Trans>
        ) : (
          <Trans>
            Your confirmation is heading to{" "}
            <SemiboldEmail data-testid="checkout-confirmation-step-client-email">
              {email}
            </SemiboldEmail>
            . We’ll follow up with tracking links once your items ship (which
            might come in a few separate packages).
          </Trans>
        )
      ) : isPaymentPending ? (
        <Trans>
          We’ll send your confirmation once the payment is processed. We’ll
          follow up with tracking links once your items ship (which might come
          in a few separate packages).
        </Trans>
      ) : (
        <Trans>
          Your confirmation is on its way. We’ll follow up with tracking links
          once your items ship (which might come in a few separate packages).
        </Trans>
      )}
    </Text>

    {createAccount ?? (
      <ContinueShopping>
        <ContinueShoppingButton />
      </ContinueShopping>
    )}
  </>
);

const CreateAccountInput: FC<{
  formik: FormikProps<CreateAccountFormValues>;
  name: keyof CreateAccountFormValues;
  placeholder: string;
  type: string;
  autoComplete: string;
  adornment?: ReactNode;
}> = ({ formik, name, placeholder, type, autoComplete, adornment }) => {
  const { _ } = useLingui();
  const errorId = useId();
  const [focused, setFocused] = useState(false);

  const fieldError = formik.errors[name];
  const error =
    formik.touched[name] && fieldError !== undefined
      ? _(fieldError)
      : undefined;

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setFocused(false);
    formik.handleBlur(event);
  };

  return (
    <div>
      <Field $isError={error !== undefined} $isFocused={focused}>
        <StyledInput
          {...formik.getFieldProps(name)}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-invalid={error !== undefined}
          aria-describedby={error ? errorId : undefined}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          data-testid={`checkout-confirmation-create-account-${name}`}
        />
        {adornment}
      </Field>

      {error && (
        <Error as="div" id={errorId} role="alert">
          {error}
        </Error>
      )}
    </div>
  );
};

const ConfirmationCreateAccount: FC = () => {
  const { _, i18n } = useLingui();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [handover] = useState(() => getCheckoutCustomer());

  const { formik, submitted, failed, errorMessage } = useCreateAccountForm({
    name: getCheckoutCustomerName(handover),
  });

  useEffect(() => {
    if (submitted) {
      rememberCheckoutSignUp();
    }
  }, [submitted]);

  return (
    <CreateAccountCard data-testid="checkout-confirmation-create-account">
      {submitted || handover.signedUp ? (
        <ConfirmationNotice mt={0}>
          <Trans>All set!</Trans>
        </ConfirmationNotice>
      ) : (
        <CreateAccountForm noValidate onSubmit={formik.handleSubmit}>
          <CreateAccountInput
            formik={formik}
            name="email"
            type="email"
            autoComplete="email"
            placeholder={t(i18n)`Email`}
          />

          <CreateAccountInput
            formik={formik}
            name="password"
            type={passwordVisible ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t(i18n)`sign_up_checkout.password_placeholder`}
            adornment={
              <PasswordToggle
                type="button"
                aria-label={
                  passwordVisible
                    ? _(msg`Hide password`)
                    : _(msg`Show password`)
                }
                onClick={() => setPasswordVisible((visible) => !visible)}
                data-testid="checkout-confirmation-create-account-password-toggle"
              >
                {passwordVisible ? <EyeSlash /> : <Eye />}
              </PasswordToggle>
            }
          />

          {failed && (
            <Error as="div" role="alert">
              {errorMessage ?? <Trans>Something went wrong</Trans>}
            </Error>
          )}

          <CreateAccountButton
            type="submit"
            variant="secondary"
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
            data-testid="checkout-confirmation-create-account-button"
          >
            <Trans>Create an account</Trans>
          </CreateAccountButton>
        </CreateAccountForm>
      )}
    </CreateAccountCard>
  );
};

const ConfirmationSubscribe: FC<{ email?: string | null }> = ({ email }) => {
  const { _, i18n } = useLingui();
  const errorId = useId();
  const [isFocused, setIsFocused] = useState(false);

  const { formik, submitted, error, loading } = useOptInForm({
    origin: Origin.Checkout,
    trackingOrigin: "confirmation_page",
    initialEmail: email,
  });

  const emailError =
    formik.submitCount > 0 && formik.errors.email !== undefined
      ? _(formik.errors.email)
      : undefined;

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    formik.handleBlur(event);
  };

  useEffect(() => {
    if (submitted) {
      rememberCheckoutOptIn();
    }
  }, [submitted]);

  return (
    <ConfirmationCard data-testid="checkout-confirmation-subscribe">
      <Heading as="h2" variant="h4">
        <Trans>Subscribe. Get Perks.</Trans>
      </Heading>

      {submitted ? (
        <ConfirmationNotice>
          <Trans>All set!</Trans>
        </ConfirmationNotice>
      ) : (
        <SubscribeFormWrapper noValidate onSubmit={formik.handleSubmit}>
          <Field $isError={emailError !== undefined} $isFocused={isFocused}>
            <StyledInput
              {...formik.getFieldProps("email")}
              type="email"
              autoComplete="email"
              placeholder={t(i18n)`Your email here`}
              aria-invalid={emailError !== undefined}
              aria-describedby={emailError ? errorId : undefined}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              data-testid="checkout-confirmation-subscribe-email"
            />
          </Field>

          {emailError && (
            <Error as="div" id={errorId} role="alert">
              {emailError}
            </Error>
          )}

          <Box mt={3}>
            <SubscribeButton
              type="submit"
              variant="secondary"
              fullWidth
              loading={loading}
              disabled={loading}
              data-testid="checkout-confirmation-subscribe-button"
            >
              <Trans>Subscribe</Trans>
            </SubscribeButton>
          </Box>

          {error && (
            <Text as="p" mt={3} color="semantic.red" fontSize={0}>
              <Trans>Something went wrong</Trans>
            </Text>
          )}
        </SubscribeFormWrapper>
      )}

      <SubscribeDisclaimer>
        <Trans>
          By submitting your contact details, and pressing the subscribe button,
          you consent to receiving marketing by email and or SMS in accordance
          with our{" "}
          <UnderlinedLink
            href={getI18nRoute({ route: "/privacy-statement", locale })}
          >
            Privacy Statement
          </UnderlinedLink>
          .
        </Trans>
      </SubscribeDisclaimer>
    </ConfirmationCard>
  );
};

const StoreLink: FC<{ platform: MobileOS; label: ReactNode }> = ({
  platform,
  label,
}) => {
  const { Icon, store, testId } = STORES[platform];
  const href = platform === "ios" ? appStoreUrl(locale) : googlePlayUrl(locale);

  return (
    <StoreButton>
      <Button
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="primary"
        colorScheme="light"
        fullWidth
        leftIcon={<Icon fill="currentColor" />}
        onClick={() =>
          void trackAppDownloadButtonClickedEvent({
            origin: APP_DOWNLOAD_ORIGIN.confirmationPage,
            store,
            destinationUrl: href,
          })
        }
        data-testid={testId}
      >
        {label}
      </Button>
    </StoreButton>
  );
};

const ConfirmationAppDownload: FC = () => {
  const { mobileOS, resolved } = useMobileOS();

  return (
    <AppDownloadCard data-testid="checkout-confirmation-app-download">
      <Heading as="h2" variant="h4">
        <Trans>Download the Otrium app.</Trans>
      </Heading>

      <Text as="p" mt={2} variant="body.source.regular16">
        <Trans>The best fashion deals, all in one place.</Trans>
      </Text>

      <AppDownloadRow>
        <AppDownloadQr aria-hidden="true" />

        <AppDownloadButtons>
          {resolved &&
            (mobileOS ? (
              <StoreLink
                platform={mobileOS}
                label={<Trans>Download app</Trans>}
              />
            ) : (
              <>
                <StoreLink platform="ios" label="App Store" />
                <StoreLink platform="android" label="Google Play" />
              </>
            ))}
        </AppDownloadButtons>
      </AppDownloadRow>
    </AppDownloadCard>
  );
};

const ConfirmationFeedbackSurvey: FC<{
  isLoggedIn: boolean;
  isSessionLoading?: boolean;
}> = ({ isLoggedIn, isSessionLoading = false }) => {
  const { _, i18n } = useLingui();
  const headingId = useId();
  const answerId = useId();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [stage, setStage] = useState<SurveyStage>("asking");
  const [answer, setAnswer] = useState("");
  const [finishing, setFinishing] = useState(false);
  const animateConfirmation = useRef(false);

  const {
    id: formId,
    question,
    options,
  } = isLoggedIn
    ? FEEDBACK_FORMS.didYouFindEverything
    : FEEDBACK_FORMS.howDidYouHear;

  const selected = options.find((option) => option.id === selectedId);

  const storedFeedback = useRef<StoredFeedback>();

  if (!storedFeedback.current) {
    storedFeedback.current = getStoredFeedback();
  }

  const alreadyAnswered = Boolean(storedFeedback.current[formId]);

  const [ref] = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange(inView) {
      if (inView && !alreadyAnswered) {
        void trackFeedbackFormDisplayedEvent({ question });
      }
    },
  });

  const answered = useRef(false);

  useEffect(() => {
    answered.current = alreadyAnswered;
    animateConfirmation.current = false;
    setSelectedId(undefined);
    setAnswer("");
    setFinishing(false);
    setStage(alreadyAnswered ? "confirmed" : "asking");
  }, [alreadyAnswered, formId]);

  useEffect(() => {
    if (!selected) {
      return;
    }

    const followUp = Boolean(selected.followUp);
    const timeout = setTimeout(
      () => {
        if (followUp) {
          setStage("followUp");
          return;
        }

        animateConfirmation.current = true;
        setStage("confirmed");
      },
      followUp ? FEEDBACK_FOLLOW_UP_DELAY_MS : FEEDBACK_CONFIRMATION_DELAY_MS
    );

    return () => clearTimeout(timeout);
  }, [selected]);

  useEffect(() => {
    if (!finishing) {
      return;
    }

    const timeout = setTimeout(() => {
      animateConfirmation.current = true;
      setStage("confirmed");
    }, FEEDBACK_SUBMIT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [finishing]);

  const handleSelect = (option: FeedbackOption): void => {
    if (answered.current) {
      return;
    }

    answered.current = true;
    setSelectedId(option.id);
    storeFeedbackAnswer(formId, { optionId: option.id });
    void trackFeedbackFormAnsweredEvent({ question, answer: option.answer });
  };

  const finish = (): void => setFinishing(true);

  const followUpAnswer = answer.trim();

  const submitFollowUp = (): void => {
    if (finishing) {
      return;
    }

    if (selected?.followUp && followUpAnswer) {
      storeFeedbackAnswer(formId, {
        optionId: selected.id,
        followUpAnswer,
      });
      void trackFeedbackFormCompletedEvent({
        question,
        answer: selected.answer,
        followUpQuestion: selected.followUp.question,
        followUpAnswer,
      });
    }

    finish();
  };

  const visibleOptions =
    stage === "followUp" && selected ? [selected] : options;

  const Options = isLoggedIn ? FeedbackRows : FeedbackGrid;

  if (isSessionLoading) {
    return null;
  }

  return (
    <Box
      ref={ref}
      role="group"
      aria-labelledby={headingId}
      data-testid="checkout-confirmation-feedback"
      data-form-id={formId}
    >
      <Heading id={headingId} as="h2" variant="h4">
        {isLoggedIn ? (
          <Trans>Did you find everything you were looking for today?</Trans>
        ) : (
          <Trans>
            How did you <u>first</u> hear about us?
          </Trans>
        )}
      </Heading>

      <Box mt={isLoggedIn ? [3, 3, 5] : 4}>
        {stage === "confirmed" ? (
          <ChoiceCardConfirmation
            label={<Trans>Noted - thank you!</Trans>}
            animate={animateConfirmation.current}
          />
        ) : (
          <>
            <Options>
              {visibleOptions.map((option) => (
                <ChoiceCard
                  key={option.id}
                  label={
                    typeof option.label === "string"
                      ? option.label
                      : _(option.label)
                  }
                  compact={isLoggedIn}
                  selected={selectedId === option.id}
                  locked={selectedId !== undefined}
                  onClick={() => handleSelect(option)}
                />
              ))}
            </Options>

            {stage === "followUp" && !finishing && selected?.followUp && (
              <>
                <FollowUp>
                  <FollowUpLabel htmlFor={answerId}>
                    {_(selected.followUp.label)}
                  </FollowUpLabel>
                  <FollowUpTextArea
                    id={answerId}
                    rows={1}
                    value={answer}
                    placeholder={t(i18n)`Answer`}
                    onChange={(event) => setAnswer(event.target.value)}
                  />
                </FollowUp>

                <FollowUpActions>
                  <SmallButton
                    variant="secondary"
                    disabled={!followUpAnswer}
                    onClick={submitFollowUp}
                    data-testid="checkout-confirmation-feedback-submit"
                  >
                    <Trans>Submit</Trans>
                  </SmallButton>
                  <SkipButton type="button" onClick={finish}>
                    <Trans>Skip</Trans>
                  </SkipButton>
                </FollowUpActions>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const ConfirmationReferAFriend: FC = () => {
  const { currentShopType } = useShopTypeContext();
  const shopType = localeFix({ gender: currentShopType });
  const { segmentPromotionClickedOrViewed } = useSegment();

  const { imgRef, loaded, onLoad } = useImageLoaded();

  const { data } = useReferFriend(shopType);
  const referFriend = data?.referFriend;

  const promotion = {
    ...REFER_A_FRIEND_PROMOTION,
    id: referFriend?.title,
    creative: referFriend?.image,
    path: referFriend?.link,
    shopType,
  };

  const [ref] = useInView({
    threshold: 0,
    triggerOnce: true,
    onChange(inView) {
      if (inView) {
        void segmentPromotionClickedOrViewed({
          ...promotion,
          eventType: PromotionEventType.view,
        });
      }
    },
  });

  if (!referFriend?.title) {
    return null;
  }

  return (
    <ReferAFriendCard
      ref={ref}
      data-testid="checkout-confirmation-refer-a-friend"
    >
      {referFriend.image && (
        <ReferAFriendImage>
          {!loaded && <Skeleton style={{ position: "absolute", inset: 0 }} />}
          <img
            ref={imgRef}
            src={referFriend.image}
            alt=""
            decoding="async"
            loading="lazy"
            onLoad={onLoad}
            style={{
              opacity: loaded ? 1 : 0,
              transition: "opacity 300ms ease",
            }}
          />
        </ReferAFriendImage>
      )}

      <ReferAFriendBody>
        <Heading as="h2" variant="h4">
          {referFriend.title}
        </Heading>

        {referFriend.subtitle && (
          <Text as="p" mt={2} variant="body.source.regular16">
            {referFriend.subtitle}
          </Text>
        )}

        {referFriend.linkCTA && referFriend.link && (
          <ReferAFriendLink>
            <UnderlinedLink
              href={referFriend.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                void segmentPromotionClickedOrViewed({
                  ...promotion,
                  eventType: PromotionEventType.click,
                })
              }
            >
              {referFriend.linkCTA}
            </UnderlinedLink>
          </ReferAFriendLink>
        )}
      </ReferAFriendBody>
    </ReferAFriendCard>
  );
};

const CheckoutStepConfirmation: FC = () => {
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();
  const { setPageViewedProperties, setSigningOut } = useAppActions();
  const signingOut = useSigningOut();
  const { i18n } = useLingui();
  const router = useRouter();

  const isPaymentPending = router.query.paymentStatus === "pending";
  const { hadOptedIn, resolved: optInResolved } = useHadOptedIn();
  const {
    firstName,
    email,
    usedSignUpBlock,
    resolved: customerResolved,
  } = useCheckoutCustomer();

  const canCreateAccount = canCreateAccountOnConfirmation({
    isLoggedIn,
    isSessionLoading,
    isHandoverResolved: customerResolved,
    usedSignUpBlock: !!usedSignUpBlock,
  });

  useEffect(() => {
    setPageViewedProperties({ page_type: "purchase" });
  }, [setPageViewedProperties]);

  useEffect(() => {
    setSigningOut(false);
  }, [setSigningOut]);

  useEffect(() => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  }, []);

  if (signingOut) {
    return (
      <>
        <PageHead title={t(i18n)`Checkout | Otrium`} />
        <ScreenLoader color="black" />
      </>
    );
  }

  return (
    <>
      <PageHead title={t(i18n)`Checkout | Otrium`} />

      <div className={styles.content}>
        <Stepper activeStep={3} />

        <div className={styles.layout}>
          <div className={styles.leftColumn}>
            <div
              className={classNames(styles.block, styles.leadBlock)}
              style={mobileOrder(MOBILE_ORDER.thanks)}
            >
              <ConfirmationThanks
                firstName={firstName}
                email={email}
                isPaymentPending={isPaymentPending}
                createAccount={
                  canCreateAccount ? <ConfirmationCreateAccount /> : undefined
                }
              />
            </div>

            <div
              className={styles.block}
              style={mobileOrder(MOBILE_ORDER.survey)}
            >
              <ConfirmationFeedbackSurvey
                isLoggedIn={isLoggedIn}
                isSessionLoading={isSessionLoading}
              />
            </div>
          </div>

          <div className={styles.rightColumn}>
            {optInResolved && !hadOptedIn && (
              <div
                className={classNames(styles.block, styles.bleedBlock)}
                style={mobileOrder(MOBILE_ORDER.subscribe)}
              >
                <ConfirmationSubscribe email={email} />
              </div>
            )}

            <div
              className={classNames(styles.block, styles.tightBlock)}
              style={mobileOrder(MOBILE_ORDER.appDownload)}
            >
              <ConfirmationAppDownload />
            </div>

            <div
              className={styles.block}
              style={mobileOrder(MOBILE_ORDER.referAFriend)}
            >
              <ConfirmationReferAFriend />
            </div>
          </div>
        </div>

        <div className={styles.stickyCta}>
          <ContinueShoppingButton sticky />
        </div>
      </div>
    </>
  );
};

export default CheckoutStepConfirmation;
export {
  ConfirmationAppDownload,
  ConfirmationCreateAccount,
  ConfirmationFeedbackSurvey,
  ConfirmationReferAFriend,
  ConfirmationSubscribe,
  ConfirmationThanks,
  ContinueShoppingButton,
  useCheckoutCustomer,
  useHadOptedIn,
};
